import React, { useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { addLooms } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { countLoomCodes } from "./loomCodes";
import "../people/quick-add.scss";

/** Loom numbers as a range ("1-12, N01-N44"): the same box on the first open and whenever more looms come. */
export const LoomRangeForm = ({
  onAdded,
  hint = "1-12",
  inputRef,
  id = "kh-looms",
  autoFocus,
}: {
  onAdded: () => void;
  hint?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  id?: string;
  autoFocus?: boolean;
}) => {
  const { t } = useKT();
  const [codes, setCodes] = useState("");
  const [busy, setBusy] = useState(false);
  const count = countLoomCodes(codes);
  const add = async () => {
    if (!codes.trim() || busy) return;
    setBusy(true);
    try {
      const res: any = await addLooms({ codes: codes.trim() });
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Something went wrong"), type: "danger" });
        return;
      }
      const skipped = (res.data?.skipped || []).length;
      notifications({
        title: t("Saved"),
        message: skipped ? t("{{n}} looms added · {{s}} were already there", { n: res.data?.added ?? 0, s: skipped }) : t("{{n}} looms added", { n: res.data?.added ?? 0 }),
        type: "success",
      });
      setCodes("");
      onAdded();
    } catch {
      notifications({ title: t("No signal"), message: t("This needs a connection. Try again in a moment."), type: "danger" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <label htmlFor={id}>
        {t("Loom numbers")} <span>{t("e.g. {{hint}}", { hint })}</span>
      </label>
      <input
        ref={inputRef}
        id={id}
        className="form-control"
        value={codes}
        onChange={(e) => setCodes(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && add()}
        placeholder={hint}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      <button type="button" className="btn ResetBtn" disabled={busy || !count} onClick={add}>
        {busy ? t("Saving…") : count === 1 ? t("Add 1 loom") : count ? t("Add {{n}} looms", { n: count }) : t("Add looms")}
      </button>
      {count === null ? <span className="kh-start__err">{t("Check the range, e.g. 1-12")}</span> : null}
    </>
  );
};

/** More looms later: a sheet from the bottom over Home, the board stays behind it. */
export const AddLoomsSheet = ({ hint, onAdded, onClose }: { hint: string; onAdded: () => void; onClose: () => void }) => {
  const { t } = useKT();
  return (
    <>
      <div className="kq-backdrop" onClick={onClose} />
      <section className="kq kh-addlooms" role="dialog" aria-modal="true" aria-label={t("Add looms")}>
        <div className="kq__grab" aria-hidden="true" />
        <h3>{t("Add looms")}</h3>
        <div className="kh-start kh-start--sheet">
          <LoomRangeForm id="kh-more-looms" hint={hint} autoFocus onAdded={onAdded} />
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
          {t("Close")}
        </button>
      </section>
    </>
  );
};
